import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from './client';
import { useTheme, type SavedTheme } from '../app/theme';
import { EMERON, type Theme, type ThemeColors } from '../theme/palette';
import { DEFAULT_THEME_ID } from '../theme/themes';
import type { components } from './schema';

type ThemeDto = components['schemas']['ThemeDto'];
type ActiveThemeDto = components['schemas']['ActiveThemeDto'];

const AREA = 'themes';

/**
 * Turns a saved theme into the shape the SPA applies. Colours are merged over ADPAD's, so a theme written before a new
 * token existed still renders — the missing one falls back to the installation's default colour rather than to nothing.
 */
export const toTheme = (dto: ThemeDto): Theme => ({
  name: dto.name,
  dark: dto.isDark,
  radius: dto.radius,
  fontSize: dto.fontSize,
  fontFamily: dto.fontFamily,
  fontFamilyAr: dto.fontFamilyAr,
  colors: { ...EMERON.colors, ...(dto.colors as Partial<ThemeColors>) },
});

/** The themes an administrator has built. Readable by everybody: you can wear any of them. */
export const useSavedThemes = () =>
  useQuery({ queryKey: [AREA, 'list'], queryFn: () => unwrap(api.GET('/api/v1/themes', {})), staleTime: 5 * 60_000 });

/** What this reader should be wearing: their own choice, or the department's default. */
export const useActiveTheme = () =>
  useQuery({ queryKey: [AREA, 'active'], queryFn: () => unwrap(api.GET('/api/v1/themes/active', {})), staleTime: 5 * 60_000 });

/**
 * Remembers a person's choice against their account, so it follows them to another machine. `null` means "follow the
 * department's default" — the choice is cleared rather than replaced.
 *
 * The answer the server will give is known before it gives it, so the active-theme query is written at once: the page
 * wears the choice immediately and a stale refetch cannot put the old theme back in the meantime.
 */
export function useChooseMyTheme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string | null) => unwrap(api.PUT('/api/v1/themes/mine', { body: { code } })),
    onMutate: (code) => {
      const built = qc.getQueryData<ThemeDto[]>([AREA, 'list']) ?? [];
      qc.setQueryData<ActiveThemeDto>([AREA, 'active'], (prev) => {
        const worn = code ?? prev?.defaultCode ?? null;
        return { code: worn, theme: built.find((dto) => dto.code === worn) ?? null, chosen: code !== null, defaultCode: prev?.defaultCode ?? null };
      });
    },
    onSettled: () => qc.invalidateQueries({ queryKey: [AREA, 'active'] }),
  });
}

export function useSaveTheme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, body }: { code?: string; body: components['schemas']['ThemeUpsert'] }) =>
      code === undefined
        ? unwrap(api.POST('/api/v1/admin/themes', { body }))
        : unwrap(api.PUT('/api/v1/admin/themes/{code}', { params: { path: { code } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: [AREA] }),
  });
}

export function useDeleteTheme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => unwrap(api.DELETE('/api/v1/admin/themes/{code}', { params: { path: { code } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: [AREA] }),
  });
}

export function useSetDefaultTheme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => unwrap(api.PUT('/api/v1/admin/themes/default', { body: { code } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: [AREA] }),
  });
}

/**
 * Wears what the server says to wear.
 *
 * The browser remembers a choice so the first paint is right with no flash; the account is the authority, and this
 * brings the page in line with it as soon as the answer arrives — which is what makes a choice follow a person to
 * another machine, and what lets an administrator set a default for everyone who has not chosen. A code naming one of
 * the themes that ship needs nothing over the wire: the client already holds those, so only a theme somebody built
 * travels.
 *
 * It used to defer to the browser's memory whenever the two disagreed, meaning the first server-driven change stuck
 * for ever: a default made on admin/themes changed the screen once and never again, on any machine, until localStorage
 * was cleared by hand. A choice made on the picker is written to the query before the request goes out
 * (`useChooseMyTheme`), so nothing here can put an older answer back over it.
 */
export function useServerTheme(signedIn: boolean): void {
  const active = useActiveTheme();
  const saved = useSavedThemes();
  const setSaved = useTheme((s) => s.setSaved);
  const wearCustom = useTheme((s) => s.wearCustom);
  const choose = useTheme((s) => s.choose);

  const list = saved.data;
  useEffect(() => {
    if (!signedIn || !list) return;
    setSaved(list.map((dto): SavedTheme => ({ code: dto.code, name: dto.name, theme: toTheme(dto) })));
  }, [signedIn, list, setSaved]);

  const answer = active.data;
  useEffect(() => {
    if (!signedIn || !answer) return;
    // no choice and no default is an answer too: the department's own look, not whatever this browser last wore
    const code = answer.code ?? DEFAULT_THEME_ID;
    if (answer.theme) wearCustom(code, toTheme(answer.theme));
    else choose(code);
    // choosing is idempotent, so re-running on a refetch costs nothing
  }, [signedIn, answer, wearCustom, choose]);
}
